import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import FolderOutlinedIcon from '@material-ui/icons/FolderOutlined';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useStyles } from './Styles';
import DocumentTable from './DocumentTable';
import { REPOSITORIES, getRepository } from './repositoryData';
import {
  fetchCMOMembersAPI,
  fetchMfgPlantsAPI,
  fetchProjectsAPI,
  fetchSavedAndRecentRepoFiltersAPI,
  saveRepoFiltersAPI,
  saveRecentRepoFiltersAPI,
  fetchPDRepositoryDocumentsAPI,
  downloadPDCustomFileAPI,
} from './documentRepositoryAPI';

/**
 * Document Repository popup (PPT "Document Repository", slides 24-35).
 *
 * The document TABLE is fetched from POST /fetchPDRepositoryDocuments
 * (fetchPDRepositoryDocumentsAPI) — Stage/Batch/Search/category scoping and the
 * per-category counts are all resolved server-side. repositoryData.js is kept
 * only for the five categories' static metadata (name/accent/banner) — its
 * dummy `documents` arrays are unused. The filters and save-filters flow:
 *   • CMO / MFG Plant / Product / Project / Campaign / Stage / Batch are loaded
 *     from live endpoints and cascade-linked in one direction, per the FSD:
 *     each dropdown's options are scoped only by the filters BEFORE it in the
 *     panel (CMO -> MFG Plant -> Product -> Project -> Campaign -> Stage ->
 *     Batch), never by the ones after it. Every filter defaults to "All ----".
 *   • "Save view" persists the whole selection through the shared save-filters
 *     API under the 'documentRepositoryDashboard' key, so a returning user
 *     reopens with their saved filters. First-time users see "All ----"
 *     everywhere. (The earlier "Save set" / "Save project" buttons were removed
 *     in the PPT redesign.)
 *   • Separately, every filter change is ALSO silently persisted under the
 *     'documentRepositoryDashboardRecent' key (no button, no confirmation) —
 *     the FSD's "most recently used values" default, which must survive the
 *     user coming back later (a reload, a different device, logging back in),
 *     not just closing and reopening the popup within the same tab.
 *   • When a filter is "all" it does not restrict that dimension; with every
 *     filter "all", all of the category's documents are listed.
 *
 * Out of scope for this release (per the brief): Export for BMS Release (button
 * shown but disabled) and the Review fields / Review queries workspace (those
 * row actions are disabled in DocumentTable).
 */

const ALL = 'all';
const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

// The single "Save view" button persists the whole filter selection (PPT
// redesign: the earlier "Save set" / "Save project" buttons were removed).
const VIEW_FIELDS = ['cmo', 'site', 'product', 'projectId', 'campaignId', 'stage', 'batch'];

// Product filter VALUE = the SPONSOR (BMS) product code; the option LABEL is
// "Product Code / Description" (deck "Filter Dropdowns" slide, e.g.
// "1462139/BMT-897624"). Projects with no sponsor product code contribute
// nothing to the Product dropdown.
const sponsorProductCodeOf = (p) =>
  p && p.productCode != null ? String(p.productCode).trim() : '';

// Stage option label = "<sponsorStageID> (Stage <cmoStageID>)" — e.g.
// "Step 1 (Stage A)" — the "mix of both BMS and Sai" the deck's Stage dropdown
// shows. Falls back to whichever id exists, then the stage's own name.
const stageLabelOf = (s) => {
  const step = s && s.sponsorStageID != null ? String(s.sponsorStageID).trim() : '';
  const cmo = s && s.cmoStageID != null ? String(s.cmoStageID).trim() : '';
  if (step && cmo) return `${step} (Stage ${cmo})`;
  if (step) return step;
  if (cmo) return `Stage ${cmo}`;
  return (s && (s.stageName || s.stageId)) || '';
};

// Add the current value as an option when it is not already present, so a saved
// selection still displays while its option list is (re)loading.
const ensureOption = (options, value, label) => {
  if (value == null || value === ALL) return options;
  if (options.some((o) => String(o.id) === String(value))) return options;
  return [{ id: value, label: label || value }, ...options];
};

const uniqueBy = (arr, keyFn) => {
  const seen = new Set();
  const out = [];
  arr.forEach((item) => {
    const k = keyFn(item);
    if (k == null || seen.has(k)) return;
    seen.add(k);
    out.push(item);
  });
  return out;
};

// A single filter dropdown (label lives above it in the panel). Hoisted to
// module scope so it is a stable component type across renders (defining it
// inside the parent would remount every Select on each render).
const FilterSelect = ({ classes, value, onChange, allLabel, options, disabled }) => (
  <Select
    variant="outlined"
    className={classes.select}
    value={value}
    onChange={onChange}
    displayEmpty
    disabled={disabled}
    MenuProps={{ PaperProps: { style: { maxHeight: 320 } }, getContentAnchorEl: null }}
  >
    <MenuItem value={ALL}>{allLabel}</MenuItem>
    {options.map((o) => (
      <MenuItem key={o.id} value={o.id}>
        {o.label}
      </MenuItem>
    ))}
  </Select>
);

const DocumentRepository = ({
  open,
  repositoryKey,
  onClose,
  recentFilters,
  onFiltersChange,
  // Deep-link overrides (e.g. the "Analytical Report" link on the In-Process
  // Tests tab): jump straight to a specific campaign/batch/search combo and
  // skip the normal "select filters, then Save view" friction — the caller
  // already knows exactly what it wants shown. All optional; omitting them
  // keeps the normal open behavior (recent/saved filters, list empty until
  // Save view).
  initialCampaignId,
  initialBatchNumber,
  initialSearch,
}) => {
  const classes = useStyles();

  // The filter selection the user last had in THIS popup (held by the parent so
  // it survives close/reopen). Read via a ref so the open effect uses the latest
  // value WITHOUT re-running (and wiping the user's selections) on every render.
  const recentFiltersRef = useRef(recentFilters);
  recentFiltersRef.current = recentFilters;

  // The filter set currently persisted under our key. Loaded on open, then
  // merged into by the Save buttons so "Save set" / "Save project" update only
  // their own fields without clobbering the rest of the saved set.
  const savedFiltersRef = useRef({});

  // Skips the NEXT firing of the "most recently used" auto-persist effect
  // below — set in two places, both restoring/resetting state rather than the
  // user actually choosing something:
  //   1. On open, right after hydrating cmo/site/... from the saved/recent
  //      values — otherwise the effect fires on every single open (its
  //      buildPayload dependency changes identity whenever the option lists
  //      reload, which happens on every open) and re-saves the same values
  //      back to the API even when the user changed nothing.
  //   2. On Clear filters — a view-level action ONLY, never persisted,
  //      matching the Product Development Dashboard's own Clear Filters (see
  //      useHandlers.js: "clearing is a view-level action, and the cleared
  //      selection only becomes the saved state once the user presses Save").
  //      Without this, clearing would silently overwrite the user's actual
  //      last meaningful selection with "All ----".
  const skipNextRecentPersistRef = useRef(false);

  const [activeKey, setActiveKey] = useState(repositoryKey || 'manufacturing');
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [switchAnchor, setSwitchAnchor] = useState(null);

  // ---- document list (real API) ----
  const [documents, setDocuments] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [docsLoading, setDocsLoading] = useState(false);

  // ---- filter option data (real APIs) ----
  const [cmoMembers, setCmoMembers] = useState([]);
  const [mfgPlants, setMfgPlants] = useState([]);
  const [projects, setProjects] = useState([]);
  // The filter panel + table are shown only once the option APIs have resolved.
  const [loading, setLoading] = useState(true);

  // ---- filter selections (all default to "All ----") ----
  const [cmo, setCmo] = useState(ALL);
  const [site, setSite] = useState(ALL);
  const [product, setProduct] = useState(ALL);
  const [projectId, setProjectId] = useState(ALL);
  const [campaignId, setCampaignId] = useState(ALL);
  const [stage, setStage] = useState(ALL);
  const [batch, setBatch] = useState(ALL);

  // ---- "saved" affordance state for the Save view button ----
  const [viewSaved, setViewSaved] = useState(false);
  // Human labels for id-valued saved selections (CMO org id, project id) so the
  // dropdowns display correctly before their option lists resolve.
  const [savedLabels, setSavedLabels] = useState({});

  // ── load option data + saved filters when the popup opens ──────────────────
  useEffect(() => {
    if (!open) return undefined;
    setActiveKey(repositoryKey || 'manufacturing');
    setSelected(new Set());
    setSearch('');
    setLoading(true);

    let cancelled = false;
    (async () => {
      // allSettled so ONE failing filter API never blocks the rest of the popup.
      const [cmoR, mfgR, projR, savedAndRecentR] = await Promise.allSettled([
        fetchCMOMembersAPI(),
        fetchMfgPlantsAPI(),
        // The cascade needs the full project → campaign → stage → batch tree,
        // so page 0 / size 0 fetches everything (no pagination).
        fetchProjectsAPI({ pageNumber: 0, pageSize: 0 }),
        // One request returns both the explicit saved view AND the "most
        // recently used" values — see fetchSavedAndRecentRepoFiltersAPI.
        fetchSavedAndRecentRepoFiltersAPI(),
      ]);
      if (cancelled) return;

      const val = (r) => (r.status === 'fulfilled' ? r.value : null);
      if (cmoR.status === 'rejected') console.error('[DocumentRepository] CMO members failed:', cmoR.reason);
      if (mfgR.status === 'rejected') console.error('[DocumentRepository] MFG plants failed:', mfgR.reason);
      if (projR.status === 'rejected') console.error('[DocumentRepository] Projects failed:', projR.reason);
      if (savedAndRecentR.status === 'rejected') console.error('[DocumentRepository] Saved/recent filters failed:', savedAndRecentR.reason);

      setCmoMembers(val(cmoR)?.data || []);
      setMfgPlants(val(mfgR)?.data || []);
      setProjects(val(projR)?.projects || []);

      const savedAndRecent = val(savedAndRecentR);
      const savedRes = savedAndRecent?.saved;
      const saved = savedRes?.status ? savedRes.save_filter_options : null;
      // Seed the "currently saved" set so the Save buttons merge into it.
      savedFiltersRef.current = saved ? { ...saved } : {};

      const recentRes = savedAndRecent?.recent;
      const recentPersisted = recentRes?.status ? recentRes.save_filter_options : null;

      // Default precedence (FSD: "the Project and Campaign default to the
      // user's most recently used values, or to the saved view where one
      // exists"):
      //   1. the filters most recently used THIS SESSION, in-memory (survives
      //      close/reopen of the popup without a network round trip),
      //   2. else the user's persisted "most recently used" values (survives
      //      a page reload or logging back in — the FSD's actual requirement),
      //   3. else the user's explicit SAVED view,
      //   4. else "All ----".
      const source = recentFiltersRef.current || recentPersisted || saved || null;
      const pick = (k) => (source && source[k] != null && source[k] !== '' ? source[k] : ALL);

      setCmo(pick('cmo'));
      setSite(pick('site'));
      setProduct(pick('product'));
      setProjectId(pick('projectId'));
      setCampaignId(pick('campaignId'));
      setStage(pick('stage'));
      setBatch(pick('batch'));
      // Labels so the CMO / Project / Product selects display correctly before
      // their option lists resolve.
      setSavedLabels((source && source.labels) || {});

      // FSD ("Filter Panel and Save View"): "changing any filter afterward
      // clears the saved state and empties the list again until the view is
      // re-saved" — the saved state is cleared by a FILTER CHANGE, not by
      // closing and reopening the popup. So if what's about to be restored is
      // exactly the persisted saved view (every one of the 7 filters matches),
      // reopen it already "View saved" with the list showing immediately,
      // rather than making the user click Save view again for a selection
      // they never touched. If the restored values are only the in-session
      // "recently used" ones (or don't match the saved view), stay unsaved —
      // that combination was never actually saved.
      const savedPick = (k) => (saved && saved[k] != null && saved[k] !== '' ? saved[k] : ALL);
      const restoredMatchesSavedView =
        !!saved && VIEW_FIELDS.every((k) => pick(k) === savedPick(k));

      if (initialCampaignId || initialBatchNumber || initialSearch) {
        // Deep-link entry: override with exactly what the caller asked for and
        // reveal the list immediately — there is no "select filters" step here.
        if (initialCampaignId) setCampaignId(initialCampaignId);
        if (initialBatchNumber) setBatch(initialBatchNumber);
        if (initialSearch) setSearch(initialSearch);
        setViewSaved(true);
      } else {
        setViewSaved(restoredMatchesSavedView);
      }
      // This open is restoring an existing selection (session, persisted
      // "recent", or saved view) — not a user-driven change. Without this,
      // the "most recently used" auto-persist effect below fires on every
      // single open regardless (its buildPayload dependency changes identity
      // whenever the option lists reload, which happens on every open), so it
      // would re-save the same values back to the API every time the popup is
      // opened, even when the user changes nothing.
      skipNextRecentPersistRef.current = true;
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, repositoryKey, initialCampaignId, initialBatchNumber, initialSearch]);

  // ── faceted filter model ─────────────────────────────────────────────────
  // Flatten the projects tree into campaign-level "units" — the grain at which
  // every filter dimension is decidable: CMO & Product are project-level; MFG
  // Plant & Campaign are campaign-level; Stage & Batch live inside a campaign.
  // Option lists (below) are derived from these units so that selecting ANY
  // filter narrows EVERY other dropdown to the values that co-occur with it
  // (the filters are mutually linked, not just parent→child).
  const units = useMemo(() => {
    const out = [];
    (projects || []).forEach((p) => {
      const cmoId =
        p.cmoOrgID != null && p.cmoOrgID !== ''
          ? String(p.cmoOrgID)
          : p.vendorMosymphonyID != null && p.vendorMosymphonyID !== ''
            ? String(p.vendorMosymphonyID)
            : '';
      const productCode = sponsorProductCodeOf(p);
      const productDescription =
        p.productDescription != null ? String(p.productDescription).trim() : '';
      const projId = String(p.id != null ? p.id : p.projectId);
      const projLabel = p.projectDisplayId || p.projectId || projId;
      const camps = p.projectDetails?.campaigns || [];
      if (camps.length === 0) {
        out.push({ cmoId, productCode, productDescription, projId, projLabel, site: '', campaignId: '', stages: [] });
        return;
      }
      camps.forEach((c) => {
        out.push({
          cmoId,
          productCode,
          productDescription,
          projId,
          projLabel,
          // MFG Plant the campaign runs at — campaignsList[].mfgPlant, now sourced
          // from the campaign's manufacturingFacility (fetchProjectDashboard);
          // cmoSite kept only as a fallback for older responses. A selected MFG
          // Plant is compared against this value.
          site: c.mfgPlant || c.cmoSite || '',
          campaignId: c.campaignId,
          stages: c.stages || [],
        });
      });
    });
    return out;
  }, [projects]);

  // FSD ("Repository Filters and Scope Controls" → Business Rules): each filter
  // is enabled only once the filter BEFORE it in the panel has a value; CMO is
  // the only one enabled by default. The option lists themselves stay faceted
  // (every selection narrows the others) — this only adds the sequential
  // enablement the FSD requires on top of that.
  const siteEnabled = cmo !== ALL;
  const productEnabled = siteEnabled && site !== ALL;
  const projectEnabled = productEnabled && product !== ALL;
  const campaignEnabled = projectEnabled && projectId !== ALL;
  const stageEnabled = campaignEnabled && campaignId !== ALL;
  const batchEnabled = stageEnabled && stage !== ALL;

  // Stage/Batch option lists are built only once their dropdown is reachable.
  const stageBatchEnabled = stageEnabled;

  // Every option list + the document scope, computed together so each dropdown
  // is narrowed by the OTHER six selections (faceting → mutually linked filters).
  const {
    cmoOptions,
    siteOptions,
    productOptions,
    projectOptions,
    campaignOptions,
    stageOptions,
    batchOptions,
    allowedCampaigns,
  } = useMemo(() => {
    // Per-dimension matchers. cmo/site are lenient (a unit missing that datum is
    // not excluded) so live rows without an org id / site never empty a list;
    // the lower dimensions match strictly against real values.
    const m = {
      cmo: (u) => cmo === ALL || (u.cmoId ? u.cmoId === String(cmo) : true),
      site: (u) => site === ALL || (u.site ? norm(u.site) === norm(site) : true),
      product: (u) => product === ALL || norm(u.productCode) === norm(product),
      project: (u) => projectId === ALL || u.projId === String(projectId),
      campaign: (u) => campaignId === ALL || u.campaignId === campaignId,
      stage: (u) => stage === ALL || (u.stages || []).some((s) => s.stageId === stage),
      batch: (u) =>
        batch === ALL ||
        (u.stages || []).some((s) => (s.batches || []).some((b) => b.batchNumber === batch)),
    };
    const keys = Object.keys(m);
    // FSD ("Repository Filters and Scope Controls" -> Business Rules): each
    // dropdown's options are scoped ONLY by the filters BEFORE it in the panel
    // (CMO -> MFG Plant -> Product -> Project -> Campaign -> Stage -> Batch) —
    // never by filters that come after it. Without this, reopening e.g.
    // Product while Campaign/Stage/Batch are still set (chosen under the OLD
    // Product) would wrongly shrink the list to just what those downstream
    // picks still happen to match, instead of "every product belonging to the
    // selected CMO and MFG Plant" as the FSD specifies.
    const ancestorsOf = (k) => {
      const ancestorKeys = keys.slice(0, keys.indexOf(k));
      return units.filter((u) => ancestorKeys.every((kk) => m[kk](u)));
    };

    // CMO — labelled from cmoMembers. Per the FSD ("'All CMOs' shows every CMO
    // the signed-in user is entitled to"), CMO is the root of the cascade —
    // its list is never narrowed by any other filter.
    const cmoBase = uniqueBy(
      (cmoMembers || []).map((mm) => ({
        id: String(mm.organization_id),
        label: mm.organization_name || String(mm.organization_id),
      })),
      (o) => o.id
    );
    const cmoOptions = ensureOption(cmoBase, cmo, savedLabels.cmo);

    // MFG Plant — options come from the fetchMfgPlants API ONLY (never derived
    // from the projects tree), narrowed by the selected CMO via the plant row's
    // vendorMosymphonyID. Label = "Site / MFG Plant" (deck, e.g. "Bidar / Unit IV");
    // VALUE = the plant short name, which is what a campaign's mfgPlant
    // (= manufacturingFacility) is compared against so a selected plant narrows
    // every OTHER filter (the `site` matcher above).
    const siteList = uniqueBy(
      (mfgPlants || [])
        .filter((p) => cmo === ALL || String(p.vendorMosymphonyID) === String(cmo))
        .map((p) => {
          const siteName = p.SiteName != null ? String(p.SiteName).trim() : '';
          const plant = p.mfgPlantShortName != null ? String(p.mfgPlantShortName).trim() : '';
          const value = plant || siteName;
          const label = siteName && plant ? `${siteName} / ${plant}` : (siteName || plant);
          return { id: value, label };
        })
        .filter((o) => o.id),
      (o) => o.id
    );
    const siteOptions = ensureOption(siteList, site, site);

    // Product — VALUE = sponsor (BMS) product code; LABEL = "code/description"
    // (deck, e.g. "1462139/BMT-897624"). Unique by code.
    const productList = uniqueBy(
      ancestorsOf('product')
        .filter((u) => u.productCode)
        .map((u) => ({
          id: u.productCode,
          label: u.productDescription ? `${u.productCode}/${u.productDescription}` : u.productCode,
        })),
      (o) => o.id
    );
    const productOptions = ensureOption(productList, product, savedLabels.product || product);

    // Project — deduped by DISPLAY label so the same project never appears twice
    // (item 2).
    const projectList = uniqueBy(
      ancestorsOf('project')
        .map((u) => ({ id: u.projId, label: u.projLabel }))
        .filter((o) => o.id && o.id !== 'null' && o.id !== 'undefined'),
      (o) => o.label
    );
    const projectOptions = ensureOption(projectList, projectId, savedLabels.projectId);

    // Campaign.
    const campaignList = uniqueBy(
      ancestorsOf('campaign')
        .filter((u) => u.campaignId)
        .map((u) => ({ id: u.campaignId, label: u.campaignId })),
      (o) => o.id
    );
    const campaignOptions = ensureOption(campaignList, campaignId, campaignId);

    // Stage / Batch — only within a chosen Project or Campaign.
    const stageOptions = stageBatchEnabled
      ? ensureOption(
          uniqueBy(
            ancestorsOf('stage').flatMap((u) =>
              (u.stages || []).map((s) => ({ id: s.stageId, label: stageLabelOf(s) }))
            ),
            (o) => o.id
          ),
          stage,
          stage
        )
      : [];
    const batchOptions = stageBatchEnabled
      ? ensureOption(
          uniqueBy(
            ancestorsOf('batch').flatMap((u) =>
              (u.stages || [])
                .filter((s) => stage === ALL || s.stageId === stage)
                .flatMap((s) =>
                  (s.batches || []).map((b) => ({ id: b.batchNumber, label: b.batchNumber }))
                )
            ),
            (o) => o.id
          ),
          batch,
          batch
        )
      : [];

    // Documents scope = campaigns allowed by the CMO/Site/Product/Project (plus
    // an explicit Campaign) selection. Stage/Batch/Search are applied server-side
    // (fetchPDRepositoryDocumentsAPI), so they are excluded here. Sent to the API
    // as campaignIds — when every scope filter is "all" this resolves to every
    // campaign in `units`, i.e. every campaign the signed-in user is entitled to
    // (units itself is already scoped that way by fetchProjectDashboard), which
    // is what keeps an unfiltered view within the FSD's entitlement rule.
    const scopeUnits = units.filter((u) => m.cmo(u) && m.site(u) && m.product(u) && m.project(u));
    const allowedCampaigns =
      campaignId !== ALL
        ? new Set([campaignId])
        : new Set(scopeUnits.map((u) => u.campaignId).filter(Boolean));

    return {
      cmoOptions,
      siteOptions,
      productOptions,
      projectOptions,
      campaignOptions,
      stageOptions,
      batchOptions,
      allowedCampaigns,
    };
  }, [
    units,
    cmoMembers,
    mfgPlants,
    cmo,
    site,
    product,
    projectId,
    campaignId,
    stage,
    batch,
    stageBatchEnabled,
    savedLabels,
  ]);

  const repo = getRepository(activeKey);

  // ── document list (real API) ─────────────────────────────────────────────────
  // campaignIds is the ONLY scope param the API needs — it already folds in
  // CMO/Site/Product/Project/Campaign (allowedCampaigns, faceted model above).
  // Stage/Batch/Search are sent as-is and applied server-side.
  const campaignIdsForQuery = useMemo(() => Array.from(allowedCampaigns), [allowedCampaigns]);

  // Search updates the list "as the user types" (FSD), so debounce just that
  // term — every other trigger (category/scope/stage/batch) fires immediately.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open || loading) return undefined;
    let cancelled = false;
    setDocsLoading(true);
    (async () => {
      const res = await fetchPDRepositoryDocumentsAPI({
        category: activeKey,
        campaignIds: campaignIdsForQuery,
        stageId: stage,
        batchNumber: batch,
        search: debouncedSearch,
      });
      if (cancelled) return;
      setDocuments(res.data.documents || []);
      setCategoryCounts(res.data.categoryCounts || {});
      setDocsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loading, activeKey, campaignIdsForQuery, stage, batch, debouncedSearch]);

  // Category count (header badge + Switch dropdown) = category total under the
  // current scope, BEFORE Stage / Batch / Search (FSD) — from the same response.
  const scopedCountOf = useCallback(
    (repository) => categoryCounts[repository.key] || 0,
    [categoryCounts]
  );

  // FSD ("Filter Panel and Save View"): the document list stays empty until the
  // user has selected filters and clicked "Save view" — changing any filter
  // afterward clears viewSaved and empties the list again. The fetch above still
  // runs on every filter change regardless, so counts/switch-repository badges
  // stay live; only the ROW DATA shown here is gated. Once Save view is clicked
  // the already-fetched `documents` reveal immediately, no extra round trip.
  const displayedDocuments = viewSaved ? documents : [];

  // ── change handlers ─────────────────────────────────────────────────────────
  // Selecting a filter clears the strictly-dependent lower filters (so an
  // invalid parent→child combination can't linger); the option lists themselves
  // are mutually linked via the faceted model above. Any change also clears the
  // "view saved" affordance.
  const handleCmoChange = (e) => {
    setCmo(e.target.value);
    setSite(ALL);
    setProduct(ALL);
    setProjectId(ALL);
    setCampaignId(ALL);
    setStage(ALL);
    setBatch(ALL);
    setViewSaved(false);
  };
  const handleSiteChange = (e) => {
    setSite(e.target.value);
    setProduct(ALL);
    setProjectId(ALL);
    setCampaignId(ALL);
    setStage(ALL);
    setBatch(ALL);
    setViewSaved(false);
  };
  const handleProductChange = (e) => {
    setProduct(e.target.value);
    setProjectId(ALL);
    setCampaignId(ALL);
    setStage(ALL);
    setBatch(ALL);
    setViewSaved(false);
  };
  const handleProjectChange = (e) => {
    setProjectId(e.target.value);
    setCampaignId(ALL);
    setStage(ALL);
    setBatch(ALL);
    setViewSaved(false);
  };
  const handleCampaignChange = (e) => {
    setCampaignId(e.target.value);
    setStage(ALL);
    setBatch(ALL);
    setViewSaved(false);
  };
  const handleStageChange = (e) => {
    setStage(e.target.value);
    setBatch(ALL);
    setViewSaved(false);
  };
  const handleBatchChange = (e) => {
    setBatch(e.target.value);
    setViewSaved(false);
  };

  const handleClearFilters = () => {
    // View-level only — never persisted (see skipNextRecentPersistRef above).
    skipNextRecentPersistRef.current = true;
    // Reset EVERY filter back to "All ----" and clear the Search term, so the
    // popup returns to its unfiltered state in one click.
    setCmo(ALL);
    setSite(ALL);
    setProduct(ALL);
    setProjectId(ALL);
    setCampaignId(ALL);
    setStage(ALL);
    setBatch(ALL);
    setSearch('');
    setViewSaved(false);
  };

  // ── save filters (shared API, our own key) ──────────────────────────────────
  // Always persist the FULL selection — a save replaces the stored value under
  // our key, so posting a subset would drop the rest.
  const buildPayload = useCallback(
    () => ({
      cmo,
      site,
      product,
      projectId,
      campaignId,
      stage,
      batch,
      labels: {
        cmo: (cmoOptions.find((o) => String(o.id) === String(cmo)) || {}).label,
        projectId: (projectOptions.find((o) => String(o.id) === String(projectId)) || {}).label,
        product: (productOptions.find((o) => String(o.id) === String(product)) || {}).label,
      },
    }),
    [cmo, site, product, projectId, campaignId, stage, batch, cmoOptions, projectOptions, productOptions]
  );

  // Persist the given fields, MERGED into whatever is already saved under our
  // key. (The store replaces the value for our key, so we merge client-side and
  // post the whole object.) Save view passes every field.
  const persistFields = useCallback(
    async (fields) => {
      const sel = { cmo, site, product, projectId, campaignId, stage, batch };
      const labels = {
        cmo: (cmoOptions.find((o) => String(o.id) === String(cmo)) || {}).label,
        projectId: (projectOptions.find((o) => String(o.id) === String(projectId)) || {}).label,
        product: (productOptions.find((o) => String(o.id) === String(product)) || {}).label,
      };
      const merged = { ...(savedFiltersRef.current || {}) };
      fields.forEach((k) => {
        merged[k] = sel[k];
      });
      merged.labels = { ...((savedFiltersRef.current || {}).labels || {}), ...labels };
      savedFiltersRef.current = merged;
      try {
        await saveRepoFiltersAPI(merged);
      } catch (e) {
        console.error('[DocumentRepository] Failed to save filters:', e);
      }
    },
    [cmo, site, product, projectId, campaignId, stage, batch, cmoOptions, projectOptions, productOptions]
  );

  // Report the live selection up to the parent (in-memory, for an instant
  // same-session close/reopen) AND persist it as "most recently used" via the
  // shared saved-filters endpoint (FSD: the Project/Campaign default must
  // survive the user coming back later, not just this browser tab — see
  // DOC_REPO_RECENT_KEY). The persist call is fire-and-forget: no loading
  // state, no error surfaced, since this is a best-effort convenience the
  // user never explicitly asked to happen (unlike handleSaveView below).
  useEffect(() => {
    if (!open || loading) return;
    const payload = buildPayload();
    if (onFiltersChange) onFiltersChange(payload);
    if (skipNextRecentPersistRef.current) {
      // Either the popup just finished (re)hydrating on open, or Clear
      // filters just fired this effect by resetting the filter state — skip
      // persisting THIS ONE change, then resume auto-persisting normally
      // from the next real filter change onward.
      skipNextRecentPersistRef.current = false;
      return;
    }
    // saveDashboardFilters catches its own errors and always resolves, so no
    // .catch() needed here — this call is already fire-and-forget by nature.
    saveRecentRepoFiltersAPI(payload);
  }, [open, loading, buildPayload, onFiltersChange]);

  // Save view → persist the whole filter selection for the user's next visit.
  const handleSaveView = () => {
    setViewSaved(true);
    persistFields(VIEW_FIELDS);
  };

  // ── selection + repository switching ────────────────────────────────────────
  const switchRepo = (key) => {
    // Retain the filters + search; only the document selection is cleared (FSD).
    setActiveKey(key);
    setSelected(new Set());
    setSwitchAnchor(null);
  };
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) =>
      prev.size === displayedDocuments.length
        ? new Set()
        : new Set(displayedDocuments.map((d) => d.id))
    );
  const clearSelection = () => setSelected(new Set());

  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Fetches one PD digitized document's original file and saves it. Same
  // fetch-bytes-then-save-blob pattern as the Chat/Custom File download
  // handlers elsewhere in the app (e.g. CommentsBox.jsx's handleDownloadFile).
  const downloadOneDocument = async (doc) => {
    if (!doc?.collabrixPDCustomFileID) return false;
    const res = await downloadPDCustomFileAPI(doc.collabrixPDCustomFileID);
    if (!res?.status || !res?.fileData?.data) {
      console.error('[DocumentRepository] Download failed for doc:', doc?.docId);
      return false;
    }
    const byteArray = new Uint8Array(res.fileData.data);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([byteArray], { type: 'application/octet-stream' }));
    a.download = res.fileName || doc.docId || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    return true;
  };

  const handleDownloadDocument = (doc) => downloadOneDocument(doc);

  // Downloads the selected rows. The FSD ("Search, Selection and Download")
  // actually specifies this should also be enabled with nothing selected and
  // then download every document in the filtered list — deliberately
  // overridden per explicit request: the button stays disabled until at
  // least one row is selected, with no "download everything" fallback.
  // Sequential (not parallel) so the browser doesn't treat a burst of
  // simultaneous downloads as a popup spam and block them.
  const handleDownloadSelected = async () => {
    const targets = displayedDocuments.filter((d) => selected.has(d.id));
    if (!targets.length || bulkDownloading) return;
    setBulkDownloading(true);
    for (const doc of targets) {
      // eslint-disable-next-line no-await-in-loop
      await downloadOneDocument(doc);
    }
    setBulkDownloading(false);
  };

  const scopeCount = scopedCountOf(repo);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      // maxWidth={false} opts out of MUI's sm/md/lg/xl breakpoint caps — this
      // popup is wider than xl. The real size lives in classes.dialogPaper
      // (viewport-filling, capped at 1720px).
      maxWidth={false}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.root}>
        {/* ---- header ---- */}
        <div className={classes.headerRow}>
          <div className={classes.headerLeft}>
            <div className={classes.iconBox} style={{ background: repo.accent }}>
              <FolderOutlinedIcon />
            </div>
            <div>
              <div className={classes.titleLine}>
                <span className={classes.title}>{repo.name}</span>
                <span className={classes.countBadge}>{scopeCount} documents</span>
              </div>
            </div>
          </div>
          <div className={classes.headerRight}>
            <Button
              variant="outlined"
              className={classes.switchBtn}
              onClick={(e) => setSwitchAnchor(e.currentTarget)}
              endIcon={<ArrowDropDownIcon />}
            >
              <span className={classes.switchDot} style={{ background: repo.accent }} />
              Switch repository
            </Button>
            <Menu
              anchorEl={switchAnchor}
              open={Boolean(switchAnchor)}
              onClose={() => setSwitchAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {REPOSITORIES.map((r) => (
                <MenuItem
                  key={r.key}
                  selected={r.key === activeKey}
                  onClick={() => switchRepo(r.key)}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: '24px' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span className={classes.switchDot} style={{ background: r.accent }} />
                    {r.name.replace(/ Documents$| Information$/, '')}
                  </span>
                  <span style={{ color: '#000', fontWeight: 600 }}>{scopedCountOf(r)}</span>
                </MenuItem>
              ))}
            </Menu>
            <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress size={34} style={{ color: '#6D28D9' }} />
            <span className={classes.loadingText}>Loading repository…</span>
          </div>
        ) : (
        <>
        {/* ---- optional scope-note banner ---- */}
        {repo.banner ? (
          <div className={classes.banner}>
            <InfoOutlinedIcon fontSize="small" style={{ color: '#8b5cf6' }} />
            <span>{repo.banner}</span>
          </div>
        ) : null}

        {/* ---- two-row filter panel (PPT redesign) ---- */}
        <div className={classes.filterPanel}>
          {/* Row 1: CMO / MFG Plant / Product */}
          <div className={classes.filterRow}>
            <div className={classes.filterField}>
              <span className={classes.filterLabel}>CMO</span>
              <FilterSelect classes={classes} value={cmo} onChange={handleCmoChange} allLabel="All CMOs" options={cmoOptions} />
            </div>
            <div className={classes.filterField}>
              <span className={classes.filterLabel}>Site / MFG Plant</span>
              <FilterSelect classes={classes} value={site} onChange={handleSiteChange} allLabel="All plants" options={siteOptions} disabled={!siteEnabled} />
            </div>
            <div className={classes.filterField}>
              <span className={classes.filterLabel}>Product Code / Description</span>
              <FilterSelect classes={classes} value={product} onChange={handleProductChange} allLabel="All products" options={productOptions} disabled={!productEnabled} />
            </div>
          </div>

          <div className={classes.filterRowDivider} />

          {/* Row 2: Project / Campaign ... Stage / Batch + Save view + Clear filters */}
          <div className={classes.filterRow}>
            <div className={classes.filterField}>
              <span className={classes.filterLabel}>Project</span>
              <FilterSelect classes={classes} value={projectId} onChange={handleProjectChange} allLabel="All projects" options={projectOptions} disabled={!projectEnabled} />
            </div>
            <div className={classes.filterField}>
              <span className={classes.filterLabel}>Campaign</span>
              <FilterSelect classes={classes} value={campaignId} onChange={handleCampaignChange} allLabel="All campaigns" options={campaignOptions} disabled={!campaignEnabled} />
            </div>

            {/* <span className={classes.filterSpacer} /> */}

            <div className={classes.filterField}>
              <span className={classes.filterLabel}>Stage</span>
              <FilterSelect
                classes={classes}
                value={stage}
                onChange={handleStageChange}
                allLabel="All stages"
                options={stageOptions}
                disabled={!stageEnabled}
              />
            </div>
            <div className={classes.filterField}>
              <span className={classes.filterLabel}>Batch</span>
              <FilterSelect
                classes={classes}
                value={batch}
                onChange={handleBatchChange}
                allLabel="All batches"
                options={batchOptions}
                disabled={!batchEnabled}
              />
            </div>
            <Button
              variant="contained"
              disableElevation
              className={`${classes.saveComboBtn} ${viewSaved ? classes.saveComboBtnSaved : ''} ${classes.filterActionAlign}`}
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSaveView}
              title="Save the whole filter view for your next visit"
            >
              {viewSaved ? 'View saved' : 'Save view'}
            </Button>
            <span
              className={classes.clearLink}
              style={{ marginLeft: '0.75rem', alignSelf: 'flex-end', paddingBottom: 9 }}
              onClick={handleClearFilters}
            >
              Clear filters
            </span>
          </div>
        </div>

        {/* ---- toolbar (search + actions) ---- */}
        <div className={classes.toolbar}>
          <TextField
            variant="outlined"
            size="small"
            className={classes.search}
            placeholder="Search documents, batches, steps…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: '#9ca3af' }} />
                </InputAdornment>
              ),
            }}
          />
          <span className={classes.selectedCount}>{selected.size} selected</span>
          <Button
            variant="outlined"
            className={classes.downloadBtn}
            startIcon={bulkDownloading ? <CircularProgress size={14} /> : <GetAppIcon />}
            disabled={selected.size === 0 || bulkDownloading}
            onClick={handleDownloadSelected}
          >
            Download
          </Button>
          {/* Export for BMS Release is out of scope for this release — shown but disabled. */}
          <Button
            variant="contained"
            className={classes.exportBtn}
            startIcon={<MailOutlineIcon />}
            disableElevation
            disabled
          >
            Export for BMS Release
          </Button>
        </div>

        {/* ---- table ---- */}
        <DocumentTable
          classes={classes}
          documents={displayedDocuments}
          loading={docsLoading}
          viewSaved={viewSaved}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onDownloadDocument={handleDownloadDocument}
        />

        {/* ---- footer ---- */}
        <div className={classes.footer}>
          <span>
            {selected.size} of {displayedDocuments.length} selected
            {selected.size > 0 && (
              <span className={classes.footerClear} onClick={clearSelection}>
                Clear selection
              </span>
            )}
          </span>
          <span className={classes.footerNote}>
            All documents are CDMO GMP source records · audit-trailed
          </span>
        </div>
        </>
        )}
      </div>
    </Dialog>
  );
};

export default DocumentRepository;
